import React, { useMemo } from "react";
import CartItem from "./CartItem";
import { Alert, Button, Snackbar, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { getCart, loadGuestCart } from "../../../Redux/Customers/Cart/Action";
import { BOX_WEIGHTS } from "../../../utils/boxPricing";

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jwt = localStorage.getItem("jwt");
  const { cart } = useSelector((store) => store);
  const [showBulkAlert, setShowBulkAlert] = useState(false);

  // Determine if we're in guest mode
  const isGuest = !jwt;

  // Get the appropriate cart items based on mode
  const cartItems = isGuest ? cart.guestCartItems : cart.cart?.cartItems;
  const totalPrice = isGuest ? cart.guestTotalPrice : cart.cart?.totalPrice;
  const totalDiscountedPrice = isGuest
    ? cart.guestTotalDiscountedPrice
    : cart.cart?.totalDiscountedPrice;
  const totalItem = isGuest ? cart.guestTotalItem : cart.cart?.totalItem;
  const discount = isGuest
    ? (cart.guestTotalPrice - cart.guestTotalDiscountedPrice)
    : cart.cart?.discounte;

  // Compute total weight from cart items
  const totalWeight = useMemo(() => {
    if (!cartItems) return 0;
    return cartItems.reduce((sum, item) => {
      const weight = BOX_WEIGHTS[item?.size] || 0;
      return sum + (weight * (item?.quantity || 1));
    }, 0);
  }, [cartItems]);

  console.log("cart ", cart, "isGuest:", isGuest);

  useEffect(() => {
    if (jwt) {
      // Authenticated: Get cart from backend
      dispatch(getCart(jwt));
    } else {
      // Guest: Load cart from sessionStorage
      dispatch(loadGuestCart());
    }
  }, [jwt, dispatch]);

  // Show bulk limit alert when triggered
  useEffect(() => {
    if (cart.bulkLimitMessage) {
      setShowBulkAlert(true);
    }
  }, [cart.bulkLimitMessage]);

  const handleCloseBulkAlert = () => {
    setShowBulkAlert(false);
  };

  return (
    <div className="">
      {/* Bulk Limit Alert Snackbar */}
      <Snackbar
        open={showBulkAlert}
        autoHideDuration={6000}
        onClose={handleCloseBulkAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseBulkAlert} severity="warning" sx={{ width: "100%" }}>
          {cart.bulkLimitMessage}
        </Alert>
      </Snackbar>

      {cartItems?.length > 0 ? (
        <div className="lg:grid grid-cols-3 lg:px-16 relative items-start pt-5 pb-10">
          <div className="lg:col-span-2 lg:px-5 bg-white">
            <div className="space-y-3">
              {cartItems.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  showButton={true}
                  isGuest={isGuest}
                />
              ))}
            </div>
          </div>
          <div className="px-5 sticky top-5 mt-5 lg:mt-0">
            <div className="border p-5 bg-white shadow-lg rounded-md">
              <Typography variant="h6" className="font-bold opacity-60 pb-4">
                PRICE DETAILS
              </Typography>
              <hr />

              <div className="space-y-3 font-semibold">
                <div className="flex justify-between pt-3 text-black">
                  <span>
                    Items ({totalItem} {totalItem === 1 ? "item" : "items"})
                  </span>
                  <span>${typeof totalPrice === 'number' ? totalPrice.toFixed(2) : (totalPrice || 0)}</span>
                </div>
                <div className="flex justify-between text-black">
                  <span>Total Weight</span>
                  <span>{totalWeight} lbs</span>
                </div>
                <div className="flex justify-between">
                  <span>Discount</span>
                  <span className="text-green-700">-${typeof discount === 'number' ? discount.toFixed(2) : (discount || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery Charges</span>
                  <span className="text-blue-600 text-sm italic">Calculated at the Time of Delivery</span>
                </div>
                <hr />
                <div className="flex justify-between font-bold text-lg">
                  <span>Total Amount</span>
                  <span className="text-green-700">
                    ${typeof totalDiscountedPrice === 'number' ? totalDiscountedPrice.toFixed(2) : (totalDiscountedPrice || 0)}
                  </span>
                </div>
              </div>

              <Button
                onClick={() => navigate("/checkout?step=2")}
                variant="contained"
                type="submit"
                sx={{
                  padding: ".8rem 2rem",
                  marginTop: "2rem",
                  width: "100%",
                }}
              >
                Check Out
              </Button>

              {isGuest && (
                <Typography
                  variant="body2"
                  className="text-gray-500 text-center mt-2"
                  sx={{ mt: 1, textAlign: "center" }}
                >
                  You'll need to sign in to complete checkout
                </Typography>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="min-h-[60vh] flex justify-center items-center flex-col">
          <div className="text-center py-5">
            <Typography variant="h6" className="font-medium">
              Your cart is empty!
            </Typography>
            <Typography variant="body2" className="text-gray-500">
              There is nothing in your bag, let's add some items
            </Typography>
          </div>
          <Button
            onClick={() => navigate("/")}
            variant="outlined"
            sx={{ py: "11px" }}
          >
            Browse Products
          </Button>
        </div>
      )}
    </div>
  );
};

export default Cart;
